import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { View, StyleSheet, Pressable, TextInput, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef, EditableOption, ProgressCell, ProgressColorKey } from "@/components/NewTable";
import { usePositions } from "../PositionsContext";
import { Position } from "../PositionCard";
import { supabase } from "@/lib/supabase-client";
import { useToolTip } from "@/hooks/useToolTip";
import { useModal } from "@/hooks/useModal";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";
import { shiftsService } from "@/services/shiftService";
import { PositionInput } from "@/components/inputs/PositionInput";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";
import Chip from "@/components/ui/Chip";
import useAuth from "@/hooks/useAuth";
import type { theme } from "@/constants/themes";

type RootStackParamList = {
  Positions: undefined;
  AddUsers: { shiftPositionId?: string };
  AssignedUsers: { shiftPositionId?: string };
};

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
}

function HeaderButton({ icon, tooltip, onPress }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }} numberOfLines={1}>
          {tooltip}
        </ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x - 60, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <MaterialCommunityIcons name={icon} size={24} color={colors.primary} />
    </Pressable>
  );
}

// Tooltip content for Location with inherit/override source
function LocationTooltipContent({
  position,
  shiftLocation,
  projectLocation,
  colors,
  projectName = "Project",
  shiftName = "Shift",
}: {
  position: Position;
  shiftLocation?: any;
  projectLocation?: any;
  colors: theme;
  projectName?: string;
  shiftName?: string;
}) {
  const hasOverride = !!position.location_override;
  const activeLocation = position.location || shiftLocation || projectLocation;
  const source = hasOverride ? "Position" : shiftLocation ? shiftName : projectLocation ? projectName : "None";

  if (!activeLocation) {
    return (
      <View style={[tooltipStyles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
        <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>No location set</ThemedText>
      </View>
    );
  }

  const address = activeLocation.address;
  const hasAddress = address && typeof address === "object";

  return (
    <View style={[tooltipStyles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
      <ThemedText style={tooltipStyles.header}>{activeLocation.name || "Location"}</ThemedText>
      {hasAddress && (
        <ThemedText style={[tooltipStyles.description, { color: colors.textSecondary }]}>
          {`${address.street || ""}, ${address.city || ""} ${address.state || ""} ${address.zip || ""}`
            .trim()
            .replace(/^,\s*/, "")}
        </ThemedText>
      )}
      <View style={[tooltipStyles.divider, { backgroundColor: colors.border }]} />
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <ThemedText style={[tooltipStyles.source, { color: colors.textSecondary }]}>From:</ThemedText>
        <Chip text={source} style={{ paddingVertical: 2, paddingHorizontal: 6 }} />
      </View>
    </View>
  );
}

// Tooltip content for Dress Code with inherit/override source
function DressCodeTooltipContent({
  position,
  shiftDressCode,
  projectDressCode,
  colors,
  projectName = "Project",
  shiftName = "Shift",
}: {
  position: Position;
  shiftDressCode?: any;
  projectDressCode?: any;
  colors: theme;
  projectName?: string;
  shiftName?: string;
}) {
  const hasOverride = !!position.dress_code;
  const activeDressCode = position.dress_code || shiftDressCode || projectDressCode;
  const source = hasOverride ? "Position" : shiftDressCode ? shiftName : projectDressCode ? projectName : "None";

  if (!activeDressCode) {
    return (
      <View style={[tooltipStyles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
        <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>No dress code set</ThemedText>
      </View>
    );
  }

  return (
    <View style={[tooltipStyles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
      <ThemedText style={tooltipStyles.header}>{activeDressCode.name || "Dress Code"}</ThemedText>
      {activeDressCode.description && (
        <ThemedText style={[tooltipStyles.description, { color: colors.textSecondary }]}>
          {activeDressCode.description}
        </ThemedText>
      )}
      <View style={[tooltipStyles.divider, { backgroundColor: colors.border }]} />
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <ThemedText style={[tooltipStyles.source, { color: colors.textSecondary }]}>From:</ThemedText>
        <Chip text={source} style={{ paddingVertical: 2, paddingHorizontal: 6 }} />
      </View>
    </View>
  );
}

const tooltipStyles = StyleSheet.create({
  tooltip: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 8,
    maxWidth: 280,
    minWidth: 200,
  },
  header: {
    fontSize: 14,
    fontWeight: "700",
  },
  description: {
    fontSize: 12,
    lineHeight: 16,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 4,
  },
  source: {
    fontSize: 11,
    fontWeight: "500",
  },
});

// Cell component for Location with right-aligned tooltip
function LocationCellWithTooltip({
  position,
  shiftLocation,
  projectLocation,
  colors,
  projectName = "Project",
  shiftName = "Shift",
}: {
  position: Position;
  shiftLocation?: any;
  projectLocation?: any;
  colors: theme;
  projectName?: string;
  shiftName?: string;
}) {
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const hasOverride = !!position.location_override;
  const activeLocation = position.location || shiftLocation || projectLocation;
  const displayName = activeLocation?.name || "—";
  const color = hasOverride ? colors.amber.text : colors.textSecondary;

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <LocationTooltipContent
        position={position}
        shiftLocation={shiftLocation}
        projectLocation={projectLocation}
        colors={colors}
        projectName={projectName}
        shiftName={shiftName}
      />,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x - 100, y: y - 130 } as any);
      toolTip.setVisible(true);
    });
  }, [position, shiftLocation, projectLocation, colors, toolTip, projectName, shiftName]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <View ref={ref} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <ThemedText style={{ color, fontSize: 12 }} numberOfLines={1}>
        {displayName}
      </ThemedText>
    </View>
  );
}

// Cell component for Dress Code with right-aligned tooltip
function DressCodeCellWithTooltip({
  position,
  shiftDressCode,
  projectDressCode,
  colors,
  projectName = "Project",
  shiftName = "Shift",
}: {
  position: Position;
  shiftDressCode?: any;
  projectDressCode?: any;
  colors: theme;
  projectName?: string;
  shiftName?: string;
}) {
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const hasOverride = !!position.dress_code;
  const activeDressCode = position.dress_code || shiftDressCode || projectDressCode;
  const displayName = activeDressCode?.name || "—";
  const color = hasOverride ? colors.purple.border : colors.textSecondary;

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <DressCodeTooltipContent
        position={position}
        shiftDressCode={shiftDressCode}
        projectDressCode={projectDressCode}
        colors={colors}
        projectName={projectName}
        shiftName={shiftName}
      />,
    );
    ref.current?.measureInWindow((x, y, width, height) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x - 150, y: y + height + 8 } as any);
      toolTip.setVisible(true);
    });
  }, [position, shiftDressCode, projectDressCode, colors, toolTip, projectName, shiftName]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <View ref={ref} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <ThemedText style={{ color, fontSize: 12 }} numberOfLines={1}>
        {displayName}
      </ThemedText>
    </View>
  );
}

const getPositionColumns = (
  positionOptions: EditableOption[],
  locationOptions: EditableOption[],
  dressCodeOptions: EditableOption[],
  colors: theme,
  shiftLocation?: any,
  projectLocation?: any,
  shiftDressCode?: any,
  projectDressCode?: any,
  projectName = "Project",
  shiftName = "Shift",
): ColumnDef<Position>[] => [
  {
    key: "position_id",
    header: "Position",
    flex: 1,
    minWidth: 150,
    accessor: (row) => row.position_id,
    searchable: true,
    searchValue: (_, row) => row.name || "",
    render: (_, row) => <ThemedText>{row.name}</ThemedText>,
    editable: {
      type: "picker",
      options: positionOptions,
      placeholder: "Select position",
    },
  },
  // {
  //   key: "supervisory",
  //   header: "Supervisor",
  //   width: 100,
  //   align: "center",
  //   searchable: false,
  //   accessor: (row) => (row.supervisory ? "true" : "false"),
  //   render: (_, row) => (
  //     <MaterialCommunityIcons
  //       name={row.supervisory ? "shield-check" : "shield-outline"}
  //       size={18}
  //       color={row.supervisory ? colors.emerald.text : colors.textSecondary}
  //     />
  //   ),
  //   tooltip: (_, row) => (row.supervisory ? "Position requires supervisor" : "No supervisor requirement"),
  //   editable: {
  //     type: "picker",
  //     options: [
  //       { value: "true", label: "Required" },
  //       { value: "false", label: "Not Required" },
  //     ],
  //   },
  // },
  {
    key: "location",
    header: "Location",
    width: 130,
    align: "center",
    searchable: false,
    accessor: (row) => row.location?.id || "",
    render: (_, row) => (
      <LocationCellWithTooltip
        position={row}
        shiftLocation={shiftLocation}
        projectLocation={projectLocation}
        colors={colors}
        projectName={projectName}
        shiftName={shiftName}
      />
    ),
    editable: {
      type: "picker",
      options: [{ value: "", label: "Inherit" }, ...locationOptions],
      placeholder: "Inherit",
    },
  },
  {
    key: "dress_code",
    header: "Dress Code",
    width: 130,
    align: "center",
    searchable: false,
    accessor: (row) => row.dress_code?.id || "",
    render: (_, row) => (
      <DressCodeCellWithTooltip
        position={row}
        shiftDressCode={shiftDressCode}
        projectDressCode={projectDressCode}
        colors={colors}
        projectName={projectName}
        shiftName={shiftName}
      />
    ),
    editable: {
      type: "picker",
      options: [{ value: "", label: "Inherit" }, ...dressCodeOptions],
      placeholder: "Inherit",
    },
  },
  {
    key: "attachments",
    header: "Files",
    width: 80,
    align: "center",
    searchable: false,
    accessor: (row) => row.attachments?.length || 0,
    render: (value, row) => (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
        <MaterialCommunityIcons
          name="paperclip"
          size={16}
          color={(row.attachments?.length || 0) > 0 ? colors.primary : colors.textSecondary}
        />
        {(row.attachments?.length || 0) > 0 && (
          <ThemedText style={{ fontSize: 12 }}>{row.attachments?.length}</ThemedText>
        )}
      </View>
    ),
    tooltip: (_, row) =>
      row.attachments?.length ? row.attachments.map((f) => f.name).join(", ") : "No files attached",
  },
  {
    key: "progress",
    header: "Progress",
    width: 100,
    align: "center",
    searchable: false,
    accessor: (row) => {
      const filledStatuses = ["QUEUED", "INVITED", "ACCEPTED", "COMPLETE"];
      const filled = row.invited_users?.filter((u) => filledStatuses.includes(u.status))?.length ?? 0;
      const needed = row.quantity || 1;
      const preferred = row.preferred_quantity ?? needed;
      const target = Math.max(needed, preferred);
      return Math.min(Math.round((filled / target) * 100), 100);
    },
    render: (value, row) => {
      const filledStatuses = ["QUEUED", "INVITED", "ACCEPTED", "COMPLETE"];
      const invited = row.invited_users?.filter((u) => filledStatuses.includes(u.status))?.length ?? 0;
      const needed = row.quantity || 1;
      const preferred = row.preferred_quantity ?? needed;

      // Determine color based on thresholds
      let colorKey: ProgressColorKey;
      if (invited === 0) {
        colorKey = "attention"; // red
      } else if (invited < needed) {
        colorKey = "pending"; // yellow
      } else if (invited < preferred) {
        colorKey = "todo"; // blue
      } else {
        colorKey = "complete"; // green
      }

      return <ProgressCell progress={value} showCompleteLabel={false} colorKey={colorKey} />;
    },
  },
  {
    key: "quantity",
    header: "Needed",
    width: 110,
    align: "center",
    searchable: false,
    accessor: (row) => row.quantity,
    editable: { type: "text", placeholder: "0", width: 50 },
  },
  {
    key: "preferred_quantity",
    header: "Preferred",
    width: 130,
    align: "center",
    searchable: false,
    accessor: (row) => row.preferred_quantity ?? row.quantity,
    editable: { type: "text", placeholder: "0", width: 50 },
  },
  {
    key: "invited",
    header: "Invited",
    width: 110,
    align: "center",
    searchable: false,
    accessor: (row) => row.invited_users?.length ?? 0,
  },
  {
    key: "accepted",
    header: "Accepted",
    width: 140,
    align: "center",
    searchable: false,
    accessor: (row) => row.invited_users?.filter((u) => u.status === "ACCEPTED").length ?? 0,
  },
  {
    key: "opened",
    header: "Opened",
    width: 110,
    align: "center",
    searchable: false,
    accessor: (row) => row.invited_users?.filter((u) => u.meta_data?.opened).length ?? 0,
  },
  {
    key: "viewed",
    header: "Viewed",
    width: 110,
    align: "center",
    searchable: false,
    accessor: (row) => row.invited_users?.filter((u) => u.meta_data?.viewed).length ?? 0,
  },
];

export default function PositionsScreen() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const {
    positions,
    loading,
    shiftId,
    shiftLocation,
    projectLocation,
    shiftDressCode,
    projectDressCode,
    addPosition,
    removePosition,
    updatePosition,
    updateUserInPosition,
  } = usePositions();
  const { openModal, closeModal } = useModal();
  const [positionOptions, setPositionOptions] = useState<EditableOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<EditableOption[]>([]);
  const [dressCodeOptions, setDressCodeOptions] = useState<EditableOption[]>([]);
  const [searchValue, setSearchValue] = useState("");

  // Display name helpers
  const projectName = displayNames?.projects?.name || "Project";
  const shiftName = displayNames?.shifts?.name || "Shift";

  // Add Position drawer state
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [newPositionId, setNewPositionId] = useState<string>("");
  const [newQuantity, setNewQuantity] = useState<number>(1);
  const [newPreferredQuantity, setNewPreferredQuantity] = useState<number | undefined>(undefined);
  const [newLocationId, setNewLocationId] = useState<string | null>(null);
  const [newDressCodeId, setNewDressCodeId] = useState<string | null>(null);

  // Convert options to LookupInput format
  const locationLookupOptions: LookupInputOption[] = useMemo(
    () => locationOptions.map((opt) => ({ id: opt.value, name: opt.label || "", icon: "map-marker" as const })),
    [locationOptions],
  );
  const dressCodeLookupOptions: LookupInputOption[] = useMemo(
    () => dressCodeOptions.map((opt) => ({ id: opt.value, name: opt.label || "", icon: "tshirt-crew" as const })),
    [dressCodeOptions],
  );

  // Determine inherited values for display
  const inheritedLocation = shiftLocation || projectLocation;
  const inheritedLocationSource = shiftLocation ? shiftName : projectLocation ? projectName : null;
  const inheritedDressCode = shiftDressCode || projectDressCode;
  const inheritedDressCodeSource = shiftDressCode ? shiftName : projectDressCode ? projectName : null;

  const handleAddPosition = useCallback(() => {
    setShowAddDrawer(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setNewPositionId("");
    setNewQuantity(1);
    setNewPreferredQuantity(undefined);
    setNewLocationId(null);
    setNewDressCodeId(null);
    setShowAddDrawer(false);
  }, []);

  const handleSavePosition = useCallback(async () => {
    if (!shiftId || !newPositionId) return;
    try {
      // Build meta_data with dress_code_id if provided
      const metaData = newDressCodeId ? { dress_code_id: newDressCodeId } : undefined;

      // Validate preferred >= needed (in case onBlur didn't fire)
      const validatedPreferred =
        newPreferredQuantity !== undefined && newPreferredQuantity < newQuantity ? newQuantity : newPreferredQuantity;

      const result = await shiftsService.createShiftPosition(
        shiftId,
        newPositionId,
        newLocationId || undefined,
        newQuantity,
        validatedPreferred,
        undefined, // supervisor
        metaData,
      );

      const positionName = positionOptions.find((p) => p.value === newPositionId)?.label || "Position";
      const locationName = newLocationId ? locationOptions.find((l) => l.value === newLocationId)?.label : undefined;
      const dressCodeName = newDressCodeId
        ? dressCodeOptions.find((d) => d.value === newDressCodeId)?.label
        : undefined;

      addPosition({
        shift_position_id: result.id,
        position_id: newPositionId,
        name: positionName,
        quantity: result.quantity || 1,
        preferred_quantity: result.preferred_quantity,
        invited_users: [],
        // Include location override if set
        location_override: newLocationId ? { location: { id: newLocationId, name: locationName } } : undefined,
        location: newLocationId ? { id: newLocationId, name: locationName } : undefined,
        // Include dress code if set
        dress_code: newDressCodeId ? { id: newDressCodeId, name: dressCodeName } : undefined,
      });

      // Reset and close
      setNewPositionId("");
      setNewQuantity(1);
      setNewPreferredQuantity(undefined);
      setNewLocationId(null);
      setNewDressCodeId(null);
      setShowAddDrawer(false);
    } catch (error) {
      console.error("Failed to create position:", error);
    }
  }, [
    shiftId,
    newPositionId,
    newQuantity,
    newPreferredQuantity,
    newLocationId,
    newDressCodeId,
    positionOptions,
    locationOptions,
    dressCodeOptions,
    addPosition,
  ]);

  const handleDeletePosition = useCallback(
    async (row: Position) => {
      if (!row.shift_position_id) return;

      const hasUsers = (row.invited_users?.length ?? 0) > 0;

      const performDelete = async () => {
        await shiftsService.deleteShiftPosition(row.shift_position_id!);
        removePosition(row.shift_position_id!);
      };

      if (hasUsers) {
        openModal(
          <ConfirmModal
            title="Delete Position"
            message={`Are you sure you want to delete ${row.name}? This will remove all assigned users.`}
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={async () => {
              closeModal();
              await performDelete();
            }}
            onCancel={closeModal}
          />,
        );
      } else {
        await performDelete();
      }
    },
    [openModal, closeModal, removePosition],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>Positions</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
      headerRight: () => <HeaderButton icon="plus" tooltip="Add Position" onPress={handleAddPosition} />,
    });
  }, [navigation, searchValue, colors, handleAddPosition]);

  // Fetch available positions, locations, and dress codes for pickers
  useEffect(() => {
    (async () => {
      // Fetch positions
      const { data: positionsData, error: positionsError } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .order("name", { ascending: true });

      if (positionsError) {
        console.error("Failed to fetch positions:", positionsError);
      } else {
        setPositionOptions(
          (positionsData ?? []).map((pos) => ({
            value: pos.id,
            label: pos.name,
          })),
        );
      }

      // Fetch locations
      const { data: locationsData, error: locationsError } = await supabase
        .schema("lists")
        .from("locations")
        .select("id, name")
        .order("name", { ascending: true });

      if (locationsError) {
        console.error("Failed to fetch locations:", locationsError);
      } else {
        setLocationOptions(
          (locationsData ?? []).map((loc) => ({
            value: loc.id,
            label: loc.name,
          })),
        );
      }

      // Fetch dress codes
      const { data: dressCodesData, error: dressCodesError } = await supabase
        .schema("lists")
        .from("dress_codes")
        .select("id, name")
        .order("name", { ascending: true });

      if (dressCodesError) {
        console.error("Failed to fetch dress codes:", dressCodesError);
      } else {
        setDressCodeOptions(
          (dressCodesData ?? []).map((dc) => ({
            value: dc.id,
            label: dc.name,
          })),
        );
      }
    })();
  }, []);

  const columns = useMemo(
    () =>
      getPositionColumns(
        positionOptions,
        locationOptions,
        dressCodeOptions,
        colors,
        shiftLocation,
        projectLocation,
        shiftDressCode,
        projectDressCode,
        projectName,
        shiftName,
      ),
    [
      positionOptions,
      locationOptions,
      dressCodeOptions,
      colors,
      shiftLocation,
      projectLocation,
      shiftDressCode,
      projectDressCode,
      projectName,
      shiftName,
    ],
  );

  const handleEdit = async (row: Position, key: string, newValue: string) => {
    if (!row.shift_position_id) return;

    try {
      if (key === "position_id") {
        // Check if there are ACCEPTED users - need confirmation before changing position
        const acceptedUsers = row.invited_users?.filter((u) => u.status === "ACCEPTED") || [];

        if (acceptedUsers.length > 0) {
          // Show confirmation modal
          const newPositionName = positionOptions.find((p) => p.value === newValue)?.label || "new position";
          openModal(
            <ConfirmModal
              title="Change Position"
              message={`This position has ${acceptedUsers.length} accepted user${acceptedUsers.length > 1 ? "s" : ""}. Changing the position will reset their status to INVITED. Continue?`}
              confirmText="Change Position"
              cancelText="Cancel"
              onConfirm={async () => {
                closeModal();
                try {
                  // Update the position
                  await shiftsService.updateShiftPosition(row.shift_position_id!, { position_id: newValue });
                  // Reset ACCEPTED users to INVITED
                  await shiftsService.updateShiftUserStatusBatch(row.shift_position_id!, "ACCEPTED", "INVITED");
                  // Update context - position name
                  updatePosition(row.shift_position_id!, { position_id: newValue, name: newPositionName });
                  // Update context - user statuses
                  acceptedUsers.forEach((u) => {
                    updateUserInPosition(row.shift_position_id!, u.id, { status: "INVITED" });
                  });
                } catch (error) {
                  console.error("Failed to update position:", error);
                }
              }}
              onCancel={closeModal}
            />,
          );
          return;
        }

        // No ACCEPTED users - proceed normally
        await shiftsService.updateShiftPosition(row.shift_position_id, { position_id: newValue });
        const newPositionName = positionOptions.find((p) => p.value === newValue)?.label || row.name;
        updatePosition(row.shift_position_id, { position_id: newValue, name: newPositionName });
        return;
      }

      // Supervisor field (boolean as string)
      if (key === "supervisory") {
        const boolValue = newValue === "true";
        await shiftsService.updateShiftPosition(row.shift_position_id, { supervisor: boolValue });
        updatePosition(row.shift_position_id, { supervisory: boolValue });
        return;
      }

      // Location field (nullable - empty string means inherit/clear override)
      if (key === "location") {
        const locationId = newValue || null;
        await shiftsService.updateShiftPosition(row.shift_position_id, { location_id: locationId });
        // If clearing, remove override; if setting, fetch location details
        if (!locationId) {
          updatePosition(row.shift_position_id, { location_override: undefined, location: undefined });
        } else {
          const locationName = locationOptions.find((l) => l.value === locationId)?.label;
          updatePosition(row.shift_position_id, {
            location_override: { location: { id: locationId, name: locationName } },
            location: { id: locationId, name: locationName },
          });
        }
        return;
      }

      // Dress code field (stored in meta_data.dress_code_id)
      if (key === "dress_code") {
        const dressCodeId = newValue || null;
        // Update meta_data with dress_code_id
        const currentMeta = (row as any).meta_data || {};
        const newMeta = { ...currentMeta, dress_code_id: dressCodeId };
        await shiftsService.updateShiftPosition(row.shift_position_id, { meta_data: newMeta });
        // Update context
        if (!dressCodeId) {
          updatePosition(row.shift_position_id, { dress_code: undefined });
        } else {
          const dressCodeName = dressCodeOptions.find((d) => d.value === dressCodeId)?.label;
          updatePosition(row.shift_position_id, { dress_code: { id: dressCodeId, name: dressCodeName } });
        }
        return;
      }

      // Numeric fields (quantity, preferred_quantity)
      const numValue = parseInt(newValue, 10);
      if (isNaN(numValue) || numValue < 0) return;

      await shiftsService.updateShiftPosition(row.shift_position_id, { [key]: numValue });
      updatePosition(row.shift_position_id, { [key]: numValue });
    } catch (error) {
      console.error(`Failed to update ${key}:`, error);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        horizontal={true}
        showsHorizontalScrollIndicator={true}
        contentContainerStyle={{ width: "auto", flexGrow: 1 }}
        style={{ flex: 1 }}
      >
        <Table<Position>
          data={positions}
          columns={columns}
          keyExtractor={(item) => item.shift_position_id!}
          loading={loading}
          loadingRowCount={8}
          searchValue={searchValue}
          sortable
          canEdit
          onEdit={handleEdit}
          defaultSort={{ key: "position_id", direction: "asc" }}
          emptyMessage="No positions configured"
          emptyIcon="account-group-outline"
          rowActions={[
            {
              icon: "account-plus",
              label: "Add Users",
              onPress: (row) => navigation.navigate("AddUsers", { shiftPositionId: row.shift_position_id }),
            },
            {
              icon: "account-group",
              label: "View Users",
              onPress: (row) => navigation.navigate("AssignedUsers", { shiftPositionId: row.shift_position_id }),
            },
            {
              icon: "trash-can-outline",
              label: "Delete",
              color: colors.red.text,
              onPress: (row) => handleDeletePosition(row),
            },
          ]}
          minActionSlots={4}
        />
      </ScrollView>

      {/* Add Position Drawer */}
      {showAddDrawer && (
        <View style={[styles.drawer, { backgroundColor: colors.background, borderLeftColor: colors.border }]}>
          <View style={[styles.drawerHeader, { borderBottomColor: colors.border }]}>
            <ThemedText type="subtitle">Add Position</ThemedText>
            <Pressable onPress={handleCloseDrawer}>
              <MaterialCommunityIcons name="close" size={24} color={colors.text} />
            </Pressable>
          </View>

          <ScrollView style={styles.drawerContent}>
            {/* Position Picker */}
            <PositionInput
              value={newPositionId || null}
              state="editing"
              label="Position *"
              placeholder="Select position"
              onChange={(id) => setNewPositionId(id || "")}
            />

            {/* Needed (Quantity) */}
            <View style={[styles.formField, { marginTop: 16 }]}>
              <ThemedText style={styles.fieldLabel}>Needed</ThemedText>
              <TextInput
                style={[styles.formInput, { borderColor: colors.border, color: colors.text }]}
                value={String(newQuantity)}
                onChangeText={(text) => setNewQuantity(parseInt(text) || 1)}
                keyboardType="numeric"
                placeholder="1"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {/* Preferred */}
            <View style={[styles.formField, { marginTop: 16 }]}>
              <ThemedText style={styles.fieldLabel}>Preferred</ThemedText>
              <TextInput
                style={[styles.formInput, { borderColor: colors.border, color: colors.text }]}
                value={newPreferredQuantity !== undefined ? String(newPreferredQuantity) : ""}
                onChangeText={(text) => {
                  const val = parseInt(text);
                  setNewPreferredQuantity(isNaN(val) ? undefined : val);
                }}
                onBlur={() => {
                  // If preferred is set but less than needed, reset to needed
                  if (newPreferredQuantity !== undefined && newPreferredQuantity < newQuantity) {
                    setNewPreferredQuantity(newQuantity);
                  }
                }}
                keyboardType="numeric"
                placeholder={String(newQuantity)}
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {/* Location */}
            <View style={{ marginTop: 16 }}>
              <LookupInput
                label="Location"
                value={newLocationId}
                options={locationLookupOptions}
                state="editing"
                icon="map-marker"
                placeholder={inheritedLocation?.name || "No location"}
                info={!newLocationId && inheritedLocationSource ? `From ${inheritedLocationSource}` : undefined}
                onChange={setNewLocationId}
              />
            </View>

            {/* Dress Code */}
            <View style={{ marginTop: 16 }}>
              <LookupInput
                label="Dress Code"
                value={newDressCodeId}
                options={dressCodeLookupOptions}
                state="editing"
                icon="tshirt-crew"
                placeholder={inheritedDressCode?.name || "No dress code"}
                info={!newDressCodeId && inheritedDressCodeSource ? `From ${inheritedDressCodeSource}` : undefined}
                onChange={setNewDressCodeId}
              />
            </View>
          </ScrollView>

          <View style={[styles.drawerFooter, { borderTopColor: colors.border }]}>
            <Pressable
              onPress={handleCloseDrawer}
              style={[styles.drawerButton, { backgroundColor: colors.backgroundSecondary }]}
            >
              <ThemedText>Cancel</ThemedText>
            </Pressable>
            <Pressable
              onPress={handleSavePosition}
              disabled={!newPositionId}
              style={[
                styles.drawerButton,
                { backgroundColor: newPositionId ? colors.primary : colors.backgroundSecondary },
              ]}
            >
              <ThemedText style={{ color: newPositionId ? "white" : colors.textSecondary }}>Save</ThemedText>
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  // Drawer styles
  drawer: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 400,
    borderLeftWidth: 1,
    zIndex: 100,
  },
  drawerHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  drawerContent: {
    flex: 1,
    padding: 16,
  },
  formField: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 12,
    marginBottom: 4,
    opacity: 0.7,
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  drawerFooter: {
    flexDirection: "row",
    gap: 12,
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  drawerButton: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 12,
    borderRadius: 8,
  },
});
